<!--suppress JSUnfilteredForInLoop -->
<template>
    <validation-observer slim v-slot="{handleSubmit, dirty, valid, pristine}" ref="formValidator">
        <transition :name="animation" mode="out-in" duration="300">
            <div v-if="hideFormOnOptionsLoading ? optionsLoading : false" key="optionsLoadingPage">
                <slot name="loading">
                    <div class="card bg-info">
                        <loading-animation/>
                        Loading data from the server
                    </div>
                </slot>
            </div>

            <div v-else-if="hideFormOnOptionsLoadingFailed ? optionsLoadingFailed : false" key="loadingFailedPage">
                <slot name="loading-failed">
                    <div class="card bg-danger">
                        <error-icon/>
                        Loading data from the server failed
                    </div>
                </slot>
            </div>

            <div v-else-if="hideFormOnSave ? loading : false" key="savingPage">
                <slot name="saving">
                    <loading-animation/>
                    Saving data to the server
                </slot>
            </div>

            <div v-else-if="successPageVisible" key="successPage">
                <slot name="success">
                    <div class="bg-success card">
                        <success-icon variant=""/>
                        Yay. The data has been send to server successfully.
                    </div>
                </slot>
            </div>

            <div v-else-if="errorPageVisible" key="errorPage">
                <slot name="form-save-error" :show-form="hideErrorPage">
                    <div class="bg-danger card">
                        <error-icon variant=""/>
                        There were some errors while communicating with the server.
                        Please <a href="#" @click.prevent="hideErrorPage()">Click here</a> to go back to the form.
                    </div>
                </slot>
            </div>

            <form v-else role="form" @submit.prevent="handleSubmit(onSubmit)" key="formPage">
                <div class="card bg-danger mb-3" v-if="errorMessage" v-html="errorMessage"></div>

                <transition :name="animation" mode="out-in">
                    <slot name="loading-failed-form" v-if="optionsLoadingFailed">
                        <div class="card bg-danger">
                            Loading data from the server failed.
                        </div>
                    </slot>
                </transition>

                <slot
                    :can-submit="!loading && !optionsLoading && (valid || pristine)"
                    :valid="valid"
                    :options="options"
                    :model="model"
                    :loading="loading"
                    :optionsLoading="optionsLoading">
                    Your form goes here!!!
                </slot>
            </form>
        </transition>
    </validation-observer>
</template>

<script>
import axios from 'secure-axios';
import BaseForm from './BaseForm';

export default {
    name          : 'post-form',
    variationType : 'form',
    extends       : BaseForm,
    props         : {
        animation : { type : String, required : false, default : null },

        initialData           : { type : Object, required : false },
        optionsUrl            : { type : String, required : false },
        optionsParams         : { type : Object, required : false },
        autoSelectFirstOption : { type : [Object, String], required : false, default : null },

        ajaxMethod : { type : String, default : 'form' },

        saveUrl    : { type : String, required : false },
        saveParams : { type : Object, required : false },

        preSubmit : { type : Function, required : false },

        loadInitialFromOptions          : { type : Boolean, default : true },
        loadInitialFromOptionsParameter : { type : String, default : 'initial' },

        hideFormOnOptionsLoadingFailed : { type : Boolean, default : false },
        hideFormOnOptionsLoading       : { type : Boolean, default : false },
        hideFormOnSave                 : { type : Boolean, default : false },

        showErrorPageOnFailed : { type : Boolean, default : false },

        showNotificationOnFailed : { type : Boolean, default : false },
        failedNotificationText   : { type : String, default : 'Some errors occurred while communicating with server. Please try again later.' },

        showNotificationOnOptionsFailed : { type : Boolean, default : false },
        failedNotificationOptionsText   : { type : String, default : 'Some errors occurred while communicating with server. Please try again later.' }
    },

    data () {
        return {
            errorMessage : '',
            loading      : false,

            optionsLoaded        : false,
            optionsLoading       : false,
            optionsLoadingFailed : false,

            options : {},
            model   : {},

            errorPageVisible   : false,
            confirmPageVisible : false,
            successPageVisible : false
        };
    },

    watch : {
        optionsParams : function () {
            this.reset();
            this.reloadOptions();
        }
    },

    created () {
        this.loadOptions();
        this.reset();
    },

    methods : {
        setModelValue (key, value) {
            this.$set(this.model, key, value);
        },
        getModelValue (key) {
            return this.model[key];
        },

        showSuccessPage () {
            this.successPageVisible = true;
            this.confirmPageVisible = false;
            this.errorPageVisible = false;
        },
        hideSuccessPage () {
            this.successPageVisible = false;
        },

        showErrorPage () {
            this.successPageVisible = false;
            this.confirmPageVisible = false;
            this.errorPageVisible = true;
        },
        hideErrorPage () {
            this.errorPageVisible = false;
        },

        resetModel () {
            const initialData = this.initialData;
            if (initialData) {
                this.model = {
                    ...initialData
                };
            }
        },
        reset () {
            this.resetModel();
            this.loading = false;

            this.optionsLoading = !!this.optionsUrl;
            this.optionsLoaded = false;
            this.optionsLoadingFailed = false;
            this.successPageVisible = false;
        },

        reloadOptions () {
            this.optionsLoaded = false;
            this.optionsLoadingFailed = false;
            this.successPageVisible = false;

            this.loadOptions();
        },
        loadOptions () {
            if (this.optionsLoaded) {
                return;
            }
            const that = this;
            const url = this.optionsUrl;
            if (!url) {
                return;
            }
            this.optionsLoading = true;
            const params = this.optionsParams || {};
            axios.perform(url, params, this.ajaxMethod).then(function (response) {
                that.loadOptionsSuccess(response);
            }).catch(function () {
                that.loadOptionsFailed();
            });
        },
        loadOptionsSuccess (response) {
            this.options = response.data;
            this.optionsLoading = false;
            this.optionsLoaded = true;

            const mapping = this.autoSelectFirstOption;
            let modelUpdates = {};
            if (mapping) {
                modelUpdates = this.loadFirstFromOptions(mapping);
            }

            const loadInitial = this.loadInitialFromOptions;
            const loadInitialParams = this.loadInitialFromOptionsParameter;

            if (loadInitial && loadInitialParams) {
                modelUpdates = {
                    ...modelUpdates,
                    ...this.options[loadInitialParams]
                };
            }

            this.updateModal(modelUpdates);

            this.$emit('optionsLoaded', this.options, this.updateModal, this.loadFirstFromOptions);
        },
        loadOptionsFailed () {
            this.optionsLoading = false;
            this.optionsLoadingFailed = true;
            if (this.showNotificationOnOptionsFailed) {
                this.$notifyError(this.failedNotificationOptionsText);
            }
        },

        updateModal (updates) {
            if (!updates) {
                return;
            }
            const model = this.model;
            this.model = {
                ...model,
                ...updates
            };
        },
        loadFirstFromOptions (mapping, returnObject = false) {
            const modelUpdates = {};
            const options = this.options;

            for (const key in mapping) {
                // eslint-disable-next-line no-prototype-builtins
                if (mapping.hasOwnProperty(key)) {
                    const optionsKey = mapping[key];
                    const values = options[optionsKey];
                    if (values && values.length > 0) {
                        const value = values[0];
                        modelUpdates[key] = value.value;
                    }
                }
            }
            if (returnObject === true) {
                return modelUpdates;
            }
            this.updateModal(modelUpdates);
        },

        onSubmit () {
            const preSubmit = this.preSubmit;

            if (preSubmit) {
                preSubmit(this.model, this.actualSubmit);
            } else {
                this.actualSubmit();
            }
        },
        actualSubmit () {
            const that = this;
            this.loading = true;
            if (this.hideFormOnSave) {
                this.confirmPageVisible = false;
            }
            let saveParams = this.saveParams;
            if (!saveParams) {
                saveParams = {};
            }
            const data = {
                ...saveParams,
                ...this.model
            };

            const url = this.saveUrl;
            if (!url) {
                this.onSubmitSuccess(data);
            } else {
                axios.perform(url, data, this.ajaxMethod).then(function (response) {
                    that.onSubmitSuccess(response);
                }).catch(function (error) {
                    that.onSubmitFailed(error);
                });
            }
        },
        onSubmitSuccess (response) {
            this.loading = false;
            const json = response.data;
            if (json.error === false) {
                if (json.msg) {
                    this.$notify(json.msg, 'Message', {
                        type : 'success'
                    });
                }
                this.$emit('success', response);
            } else {
                this.$emit('failure', response);
                const formValidator = this.$refs.formValidator;
                formValidator.setErrors(json.errors);
                if (json.msg) {
                    this.$notify(json.msg, 'Message', {
                        type : 'info'
                    });
                } else if (json.errors === '') {
                    this.$notifyError('Please fix the errors');
                }
                this.errorMessage = json.errors.__all__ || '';
            }
        },
        onSubmitFailed (error) {
            this.successPageVisible = false;
            this.loading = false;

            if (this.showNotificationOnFailed) {
                this.$notifyError(this.failedNotificationText);
            }
            if (this.showErrorPageOnFailed) {
                this.confirmPageVisible = false;
                this.errorPageVisible = true;
            }
            this.$emit('error', error);
        }
    }
};
</script>
